import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number | string | undefined | null, currency = "EUR"): string {
  if (amount === undefined || amount === null || amount === '') return '0,00 €'
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(num)) return '0,00 €'
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

/**
 * Maps SAP center code (WERKS) to center name
 * Norm: WERKS 1000 = "TENERIFE", WERKS 2000 = "LA PALMA"
 */
export function getCenterName(werks: string): string {
  const centerMap: Record<string, string> = {
    '1000': 'TENERIFE',
    '2000': 'LA PALMA',
  }
  return centerMap[werks] || werks // Return original if not mapped
}

function parseDate(value: string | Date): Date {
  if (value instanceof Date) return value

  if (/^\d{8}$/.test(value)) {
    const year = Number(value.slice(0, 4))
    const month = Number(value.slice(4, 6)) - 1
    const day = Number(value.slice(6, 8))
    return new Date(year, month, day)
  }

  return new Date(value)
}

export function formatDate(date: string | Date): string {
  const d = parseDate(date)
  if (Number.isNaN(d.getTime())) return "-"
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d)
}

export function formatDateTime(date: string | Date): string {
  const d = parseDate(date)
  if (Number.isNaN(d.getTime())) return "-"
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d)
}

export function stripLeadingZeros(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return ''
  return String(value).replace(/^0+/, '') || '0'
}

export function padLeft(value: string | number, length: number, char = "0"): string {
  const str = String(value)
  return str.padStart(length, char)
}

export function padRight(value: string | number, length: number, char = " "): string {
  const str = String(value)
  return str.padEnd(length, char)
}

/**
 * Validates if a password meets security requirements
 * - Minimum 8 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 digit
 * - At least 1 special symbol
 */
export function isSecurePassword(password: string): boolean {
  const minLength = 8
  const hasUpperCase = /[A-Z]/.test(password)
  const hasLowerCase = /[a-z]/.test(password)
  const hasDigit = /\d/.test(password)
  const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)

  return (
    password.length >= minLength &&
    hasUpperCase &&
    hasLowerCase &&
    hasDigit &&
    hasSymbol
  )
}

export function generateSecurityCode(): string {
  return Math.floor(100000 + Math.random() * 900000).toString()
}

export type DocumentFlowAmount = { base?: number; igic?: number; total?: number }
export type DocumentFlowAmountMap = Record<string, DocumentFlowAmount>

export function parseDocumentFlowRows(payload: unknown): unknown[] {
  if (!payload || typeof payload !== 'object') return []

  const data = payload as {
    amounts?: DocumentFlowAmountMap
    data?: {
      tables?: {
        TT_FLUJO?: unknown[]
        VBFA_TAB?: unknown[]
        T_DATOS?: unknown[]
      }
      TT_FLUJO?: unknown[]
      VBFA_TAB?: unknown[]
      T_DATOS?: unknown[]
    }
  }

  const rows =
    data.data?.tables?.TT_FLUJO ||
    data.data?.TT_FLUJO ||
    data.data?.tables?.VBFA_TAB ||
    data.data?.VBFA_TAB ||
    data.data?.tables?.T_DATOS ||
    data.data?.T_DATOS ||
    []

  const result = Array.isArray(rows) ? rows : []
  ;(result as unknown as { amounts?: DocumentFlowAmountMap }).amounts = data.amounts
  return result
}

export type DocumentFlowSummaryRow = {
  documento: string
  tipo: string
  tone: string
  fecha: string
  base?: number
  igic?: number
  total?: number
}

export function lookupFlowAmount(
  amounts: DocumentFlowAmountMap | undefined,
  documento: string
): DocumentFlowAmount | undefined {
  if (!amounts || !documento) return undefined
  if (amounts[documento]) return amounts[documento]
  const stripped = documento.replace(/^0+/, '') || documento
  if (amounts[stripped]) return amounts[stripped]
  const match = Object.entries(amounts).find(
    ([k]) => (k.replace(/^0+/, '') || k) === stripped
  )
  return match?.[1]
}

type DocumentFlowRawRow = {
  VBELV?: string
  VBELN?: string
  VBTYP_V?: string
  VBTYP_N?: string
  ERDAT?: string
}

export function summarizeDocumentFlowRows(rows: unknown, excludeDocumento?: string, directNeighborsOnly = false): DocumentFlowSummaryRow[] {
  if (!Array.isArray(rows)) return []

  const amounts = (rows as unknown as { amounts?: DocumentFlowAmountMap }).amounts
  const seen = new Set<string>()
  const excludeStripped = excludeDocumento
    ? (excludeDocumento.replace(/^0+/, '') || excludeDocumento)
    : ''

  const pushRow = (
    acc: DocumentFlowSummaryRow[],
    documento: string | undefined,
    tipo: string | undefined,
    fecha: string | undefined
  ): DocumentFlowSummaryRow[] => {
    const doc = documento?.trim()
    const t = tipo?.trim()
    if (!doc || !t || seen.has(doc)) return acc
    if (excludeStripped && (doc.replace(/^0+/, '') || doc) === excludeStripped) return acc
    const meta = getDocumentFlowTypeMeta(t)
    if (!meta) return acc
    seen.add(doc)
    const amt = lookupFlowAmount(amounts, doc)
    acc.push({
      documento: doc,
      tipo: meta.label,
      tone: meta.tone,
      fecha: fecha?.trim() || '',
      base: amt?.base,
      igic: amt?.igic,
      total: amt?.total
    })
    return acc
  }

  const centerMatches = excludeStripped
    ? rows.some((item) => {
        if (!item || typeof item !== 'object') return false
        const row = item as DocumentFlowRawRow
        const origen = row.VBELV?.trim()
        const documento = row.VBELN?.trim()
        return (
          (origen && (origen.replace(/^0+/, '') || origen) === excludeStripped) ||
          (documento && (documento.replace(/^0+/, '') || documento) === excludeStripped)
        )
      })
    : false

  if (directNeighborsOnly && excludeStripped && centerMatches) {
    const info = new Map<string, { documento: string; tipo?: string; fecha?: string }>()
    const setIfMissing = (raw: string | undefined, tipo: string | undefined, fecha?: string) => {
      const doc = raw?.trim()
      if (!doc) return
      const key = doc.replace(/^0+/, '') || doc
      if (!info.has(key)) info.set(key, { documento: doc, tipo: tipo?.trim(), fecha: fecha?.trim() })
    }
    for (const item of rows) {
      if (!item || typeof item !== 'object') continue
      const row = item as DocumentFlowRawRow
      setIfMissing(row.VBELN, row.VBTYP_N, row.ERDAT)
      setIfMissing(row.VBELV, row.VBTYP_V)
    }

    const acc: DocumentFlowSummaryRow[] = []
    for (const item of rows) {
      if (!item || typeof item !== 'object') continue
      const row = item as DocumentFlowRawRow
      const origen = row.VBELV?.trim()
      const documento = row.VBELN?.trim()
      const origenKey = origen ? (origen.replace(/^0+/, '') || origen) : ''
      const docKey = documento ? (documento.replace(/^0+/, '') || documento) : ''
      if (origenKey && docKey === excludeStripped) {
        const pred = info.get(origenKey)
        pushRow(acc, pred?.documento ?? origen, pred?.tipo, pred?.fecha)
      } else if (docKey && origenKey === excludeStripped) {
        pushRow(acc, documento, row.VBTYP_N, row.ERDAT)
      }
    }
    return acc
  }

  return rows.reduce<DocumentFlowSummaryRow[]>((acc, item) => {
    if (!item || typeof item !== 'object') return acc

    const row = item as DocumentFlowRawRow

    const origen = row.VBELV?.trim()
    const documento = row.VBELN?.trim()
    const tipo = row.VBTYP_N?.trim()

    if (!origen) return acc

    return pushRow(acc, documento, tipo, row.ERDAT)
  }, [])
}

function getDocumentFlowTypeMeta(tipo: string): { label: string; tone: string } | null {
  switch (tipo) {
    case 'C':
    case 'L':
      return { label: 'Pedido', tone: 'bg-gray-100 text-gray-700' }
    case 'J':
      return { label: 'Albarán', tone: 'bg-blue-50 text-blue-700' }
    case 'M':
      return { label: 'Factura', tone: 'bg-green-50 text-green-700' }
    case 'H':
    case 'O':
    case 'K':
      return { label: 'Abono', tone: 'bg-amber-50 text-amber-700' }
    case 'T':
      return { label: 'Albarán abono', tone: 'bg-orange-50 text-orange-700' }
    default:
      return null
  }
}

export function getDocumentFlowRoute(documento: string, tipoLabel: string): string | null {
  switch (tipoLabel) {
    case 'Pedido':
    case 'Abono':
    case 'Transfer':
      return `/orders?searchType=documento&searchText=${encodeURIComponent(documento)}`
    case 'Albarán':
    case 'Albarán abono':
      return `/delivery-notes?searchType=albaran&searchText=${encodeURIComponent(documento)}`
    case 'Factura':
      return `/invoices?factura=${encodeURIComponent(documento)}`
    default:
      return null
  }
}

export function isValidDispatchDate(date: string | undefined | null): boolean {
  if (!date) return false
  if (date === '00000000' || date === '18991130' || date === '1899-11-30' || date === '30.11.1899') return false
  const d = parseDate(date)
  if (isNaN(d.getTime())) return false
  return d.getFullYear() > 1900
}

export function formatDispatchDate(date: string | undefined | null): string {
  if (isValidDispatchDate(date)) {
    return formatDate(date!)
  }
  return 'Pte.Despacho'
}

export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

export type InvoiceDueStatus = 'overdue' | 'not_due' | 'no_due_date'

export function getInvoiceDueStatus(vencimiento?: string | null): InvoiceDueStatus {
  if (!vencimiento || vencimiento === '00000000') return 'no_due_date'
  const d = parseDate(vencimiento)
  if (isNaN(d.getTime())) return 'no_due_date'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  if (dueDate < today) return 'overdue'
  return 'not_due'
}

export function isInvoiceOverdue(vencimiento?: string | null): boolean {
  return getInvoiceDueStatus(vencimiento) === 'overdue'
}

export function isInvoiceNotDue(vencimiento?: string | null): boolean {
  return getInvoiceDueStatus(vencimiento) === 'not_due'
}
